import { useAccountsInfo, useAccountsRefetch } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import { syncActiveOperationsDeltas } from '@/entities/operation';
import { useAppSettingsDictionary } from '@/shared/api';
import { usePollingTask } from '@/shared/lib';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const useAccountsAndOperationsRefresh = () => {
  const queryClient = useQueryClient();
  const { session } = useSessionStore();
  const userId = session?.userId;

  const { refetchAll: refetchAccounts, refetchRestsOnly } = useAccountsRefetch({
    userId,
  });

  const { accountsInfo } = useAccountsInfo();
  const { appSettingsData } = useAppSettingsDictionary();

  const accountsIntervalMs = accountsInfo?.restsCacheLifeTime;
  const isAutoRefreshEnabled = accountsInfo?.autoRefeshCache;
  const operationsDeltaMs = appSettingsData?.value?.refreshOperationsDeltaMs;

  const refreshAccounts = useCallback(
    () => refetchAccounts({ cancelRefetch: false }),
    [refetchAccounts]
  );

  const syncOperationsDelta = useCallback(async () => {
    const hasChanges = await syncActiveOperationsDeltas(queryClient, userId, {
      skipIfBusy: true,
    });

    if (hasChanges) {
      await refetchRestsOnly({ cancelRefetch: false });
    }
  }, [queryClient, userId, refetchRestsOnly]);

  usePollingTask({
    task: refreshAccounts,
    delayMs: accountsIntervalMs,
    enabled: Boolean(isAutoRefreshEnabled),
  });

  usePollingTask({
    task: syncOperationsDelta,
    delayMs: operationsDeltaMs,
    enabled: Boolean(userId),
  });
};
